import { useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

// Capacitor LocalNotifications bridge. Schedules workout reminders on
// native iOS/Android. On web we no-op silently — there's no equivalent
// scheduling primitive that survives tab close (Notification API works
// only when the page is open).
//
// Usage:
//   const notifs = useLocalNotifications();
//   await notifs.requestPermission();
//   await notifs.scheduleWorkoutReminder({
//     id: 1001,
//     title: "Push day in 1 hour",
//     body: "Don't skip leg day",
//     at: new Date("2026-05-25T17:00:00"),
//   });
//
// IDs are integers — keep them stable per logical reminder so you can
// cancel/reschedule cleanly. A simple scheme: hash(sessionId) → int32.

const isNative = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export type ScheduledReminder = {
  id: number;
  title: string;
  body: string;
  at: Date;
  /** Optional grouping for cancel-by-tag. iOS only honors `extra`. */
  groupTag?: string;
};

export function useLocalNotifications() {
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isNative()) return false;
    try {
      const result = await LocalNotifications.requestPermissions();
      return result.display === "granted";
    } catch (e) {
      console.warn("LocalNotifications.requestPermissions failed:", e);
      return false;
    }
  }, []);

  const checkPermission = useCallback(async (): Promise<boolean> => {
    if (!isNative()) return false;
    try {
      const result = await LocalNotifications.checkPermissions();
      return result.display === "granted";
    } catch {
      return false;
    }
  }, []);

  const scheduleWorkoutReminder = useCallback(
    async (reminder: ScheduledReminder): Promise<boolean> => {
      if (!isNative()) return false;
      // Past times would fire immediately, which is a UX bug — refuse.
      if (reminder.at.getTime() <= Date.now()) {
        console.warn("scheduleWorkoutReminder: refused past time", reminder.at);
        return false;
      }
      try {
        await LocalNotifications.schedule({
          notifications: [
            {
              id: reminder.id,
              title: reminder.title,
              body: reminder.body,
              schedule: { at: reminder.at, allowWhileIdle: true },
              extra: reminder.groupTag ? { groupTag: reminder.groupTag } : undefined,
            },
          ],
        });
        return true;
      } catch (e) {
        console.warn("LocalNotifications.schedule failed:", e);
        return false;
      }
    },
    []
  );

  const cancel = useCallback(async (id: number): Promise<void> => {
    if (!isNative()) return;
    try {
      await LocalNotifications.cancel({ notifications: [{ id }] });
    } catch (e) {
      console.warn("LocalNotifications.cancel failed:", e);
    }
  }, []);

  const cancelAll = useCallback(async (): Promise<void> => {
    if (!isNative()) return;
    try {
      const { notifications } = await LocalNotifications.getPending();
      if (notifications.length > 0) {
        await LocalNotifications.cancel({
          notifications: notifications.map((n) => ({ id: n.id })),
        });
      }
    } catch (e) {
      console.warn("LocalNotifications.cancelAll failed:", e);
    }
  }, []);

  /**
   * Reconcile pending notifications with a desired set. Cancels any
   * pending reminder whose id isn't in `desired`, schedules new ones
   * for ids that aren't yet pending. Lets callers express the future
   * declaratively without manual diffing.
   */
  const reconcile = useCallback(
    async (desired: ScheduledReminder[]): Promise<void> => {
      if (!isNative()) return;
      try {
        const { notifications: pending } = await LocalNotifications.getPending();
        const desiredIds = new Set(desired.map((r) => r.id));
        const toCancel = pending.filter((n) => !desiredIds.has(n.id));
        if (toCancel.length > 0) {
          await LocalNotifications.cancel({
            notifications: toCancel.map((n) => ({ id: n.id })),
          });
        }
        const pendingIds = new Set(pending.map((n) => n.id));
        const toSchedule = desired.filter((r) => !pendingIds.has(r.id) && r.at.getTime() > Date.now());
        for (const r of toSchedule) {
          await scheduleWorkoutReminder(r);
        }
      } catch (e) {
        console.warn("LocalNotifications.reconcile failed:", e);
      }
    },
    [scheduleWorkoutReminder]
  );

  return {
    isAvailable: isNative(),
    requestPermission,
    checkPermission,
    scheduleWorkoutReminder,
    cancel,
    cancelAll,
    reconcile,
  };
}

// Stable id derivation for a session-based reminder. JS bit ops produce
// signed 32-bit ints, which is exactly what Capacitor expects for ids.
export function sessionReminderId(sessionId: string): number {
  let hash = 0;
  for (let i = 0; i < sessionId.length; i++) {
    hash = (hash << 5) - hash + sessionId.charCodeAt(i);
    hash |= 0;
  }
  // Stay positive (Capacitor docs say ids should be non-negative).
  return Math.abs(hash);
}
